export interface Service {
  id: string;
  title: string;
  shortDescription: string;
  fullDescription: string;
  icon: string;
  category: 'construction' | 'engineering';
  features?: string[];
}

export const constructionServices: Service[] = [
  {
    id: 'general-building',
    title: 'General Building Construction',
    shortDescription:
      'Comprehensive residential and commercial construction from concept to completion.',
    fullDescription: `Our company provides comprehensive general building construction services for both residential and commercial developments. From concept to completion, we manage and execute construction projects with a focus on structural integrity, quality workmanship, and adherence to project timelines.

We specialize in constructing custom homes, apartment buildings, office spaces, retail units, and mixed-use developments, ensuring that each project meets the client's functional, aesthetic, and financial requirements. Our team coordinates all phases of construction including site preparation, foundation works, structural framing, masonry, roofing, finishing, and final inspections.

By combining experienced project management with skilled tradesmen, we ensure that every building is constructed in accordance with local building codes, engineering standards, and safety regulations. We also work closely with architects, engineers, and consultants to ensure that each project is delivered efficiently while maintaining the highest level of craftsmanship.

Our goal is to deliver durable, well-constructed buildings that provide long-term value and functionality for our clients.`,
    icon: 'Building',
    category: 'construction',
    features: [
      'Custom homes and apartment buildings',
      'Office spaces and retail units',
      'Mixed-use developments',
      'Full project coordination',
      'Building code compliance',
    ],
  },
  {
    id: 'structural-concrete',
    title: 'Structural Concrete Works',
    shortDescription:
      'Specialized reinforced concrete construction for foundations, slabs, columns, and beams.',
    fullDescription: `We provide specialized structural concrete construction services that form the backbone of safe and reliable buildings. Our expertise covers the construction of reinforced concrete foundations, floor slabs, columns, beams, retaining walls, and structural frameworks for residential, commercial, and industrial projects.

Our team carefully executes every stage of the concrete construction process, including formwork installation, reinforcement placement, concrete pouring, vibration, curing, and structural quality control. We ensure that all concrete works meet structural design specifications and engineering standards to guarantee durability and load-bearing capacity.

Whether it is a raft foundation for residential houses, reinforced columns and beams for multi-story buildings, or concrete slabs for commercial structures, we focus on precision, structural stability, and long-term performance.

Through proper planning, material selection, and skilled workmanship, we ensure that every concrete structure is strong, stable, and built to last.`,
    icon: 'Layers',
    category: 'construction',
    features: [
      'Reinforced concrete foundations',
      'Floor slabs and columns',
      'Beams and retaining walls',
      'Formwork and reinforcement',
      'Quality control testing',
    ],
  },
  {
    id: 'steel-fabrication',
    title: 'Custom Steel Fabrication & Metal Works',
    shortDescription:
      'Precision steel fabrication and metal works for structural and architectural applications.',
    fullDescription: `Our custom steel fabrication and metal works division delivers precision-engineered steel components and architectural metalwork for construction projects of all scales. We combine manufacturing expertise with construction knowledge to produce structural steel elements, decorative metalwork, and custom fabrications that meet exact specifications.

From structural steel beams and columns to custom railings, gates, and architectural features, our fabrication capabilities ensure consistent quality and precise fitment. We work with various metals including steel, stainless steel, and aluminum to meet diverse project requirements.

Our integrated approach allows for better coordination between fabrication and installation, reducing delays and ensuring seamless integration with other construction elements.`,
    icon: 'Wrench',
    category: 'construction',
    features: [
      'Structural steel fabrication',
      'Architectural metalwork',
      'Custom railings and gates',
      'Precision engineering',
      'On-site installation',
    ],
  },
  {
    id: 'renovations',
    title: 'Renovations & Building Extensions',
    shortDescription:
      'Professional renovation and extension services to upgrade and expand existing structures.',
    fullDescription: `We provide professional building renovation and extension services designed to upgrade, modernize, and expand existing structures. Whether improving the functionality of a residential home or expanding a commercial facility, our team delivers solutions that enhance both the value and usability of the property.

Our renovation services include structural repairs, interior remodeling, space reconfiguration, building upgrades, and modern finishing works. For extensions, we carefully integrate new structural elements with the existing building to ensure structural stability and architectural continuity.

We manage all aspects of renovation projects including demolition, structural modifications, new construction, mechanical and electrical upgrades, and finishing works. Our team ensures minimal disruption while maintaining high-quality standards throughout the process.

By combining technical expertise with careful planning, we help clients transform outdated or limited spaces into modern, efficient, and aesthetically appealing environments.`,
    icon: 'RefreshCw',
    category: 'construction',
    features: [
      'Structural repairs and upgrades',
      'Interior remodeling',
      'Building extensions',
      'Space reconfiguration',
      'Modern finishing works',
    ],
  },
  {
    id: 'project-supervision',
    title: 'Project Supervision & Contracting',
    shortDescription:
      'Professional oversight and contracting services for efficient project execution.',
    fullDescription: `Our project supervision and contracting services ensure that construction projects are executed efficiently, safely, and in accordance with approved plans and specifications. We provide professional oversight throughout the entire construction process to maintain quality control, cost efficiency, and timely project delivery.

Our team supervises all site activities including contractor coordination, material inspections, construction progress monitoring, quality assurance, and compliance with engineering and safety standards. We act as the client's representative on-site, ensuring that all work meets the required design specifications and construction standards.

As experienced contractors, we also manage labor, materials, scheduling, subcontractors, and logistics, ensuring that every phase of construction is properly coordinated and executed.

Through effective planning, communication, and technical oversight, we help clients achieve successful project completion while maintaining high standards of quality, safety, and cost control.`,
    icon: 'ClipboardCheck',
    category: 'construction',
    features: [
      'Site supervision and monitoring',
      'Contractor coordination',
      'Quality assurance',
      'Cost and schedule management',
      'Safety compliance',
    ],
  },
];

export const engineeringServices: Service[] = [
  {
    id: 'civil-infrastructure',
    title: 'Civil Infrastructure Design',
    shortDescription:
      'Professional design services for roadways, drainage, and utility infrastructure.',
    fullDescription: `Our company provides professional civil infrastructure construction services to support residential developments, commercial projects, and community infrastructure systems.

We specialize in the construction of roadways, drainage systems, stormwater management structures, and water distribution pipelines. These infrastructure components are essential for supporting safe transportation, proper water flow, and efficient utility services within developments.

Our infrastructure services include:
- Road construction and pavement works
- Subgrade preparation and compaction
- Stormwater drainage systems
- Concrete drains and culverts
- Installation of water supply pipelines
- Utility trenching and backfilling

We ensure that all infrastructure works are executed in accordance with engineering standards, municipal requirements, and environmental considerations. Through proper planning, accurate grading, and durable construction methods, we deliver infrastructure systems that support long-term development and community growth.`,
    icon: 'Route',
    category: 'engineering',
    features: [
      'Roadway design',
      'Drainage systems',
      'Stormwater management',
      'Water distribution',
      'Utility planning',
    ],
  },
  {
    id: 'structural-design',
    title: 'Structural Designs and Modeling',
    shortDescription:
      'Comprehensive structural engineering design and 3D modeling services.',
    fullDescription: `Our structural design and modeling services provide clients with professionally engineered building solutions that ensure safety, efficiency, and code compliance. We utilize modern design software and engineering principles to develop structural systems optimized for performance and constructability.

Our team produces detailed structural drawings, calculations, and specifications for residential, commercial, and industrial buildings. We consider local building codes, environmental factors, and client requirements to deliver designs that are both practical and economically efficient.

Through 3D structural modeling, we can visualize complex structural systems, identify potential issues before construction, and coordinate with architectural and mechanical designs for seamless integration.`,
    icon: 'PenTool',
    category: 'engineering',
    features: [
      '3D structural modeling',
      'Detailed structural drawings',
      'Engineering calculations',
      'Code compliance review',
      'Design optimization',
    ],
  },
  {
    id: 'structural-assessment',
    title: 'Structural Assessment',
    shortDescription:
      'Professional evaluation of existing structures for safety and renovation planning.',
    fullDescription: `Our structural assessment services provide comprehensive evaluation of existing buildings and structures to determine their condition, safety, and suitability for continued use or renovation. This service is essential for property buyers, renovation projects, and buildings that have experienced damage or deterioration.

We conduct thorough inspections of structural elements including foundations, columns, beams, slabs, walls, and roof systems. Our assessments identify existing damage, structural deficiencies, and potential concerns that may affect building safety or performance.

Following the assessment, we provide detailed reports with findings, recommendations for repairs or strengthening, and guidance for future maintenance. Our assessments help clients make informed decisions about property purchases, renovations, and building management.`,
    icon: 'Search',
    category: 'engineering',
    features: [
      'Building condition surveys',
      'Structural inspections',
      'Damage assessment',
      'Renovation feasibility',
      'Detailed reporting',
    ],
  },
  {
    id: 'electrical-infrastructure',
    title: 'Electrical Infrastructure Designs',
    shortDescription:
      'Electrical system design for residential, commercial, and industrial projects.',
    fullDescription: `Our electrical infrastructure design services deliver comprehensive electrical system solutions for buildings and developments of all types. We design electrical distribution systems, lighting layouts, power systems, and related infrastructure to meet project requirements and safety standards.

Our designs consider load requirements, energy efficiency, code compliance, and future expansion needs. We coordinate electrical designs with architectural and structural elements to ensure practical installation and operation.

From single-family homes to commercial complexes, we provide electrical designs that are safe, efficient, and tailored to the specific needs of each project.`,
    icon: 'Zap',
    category: 'engineering',
    features: [
      'Electrical distribution design',
      'Lighting system layouts',
      'Power system planning',
      'Load calculations',
      'Energy efficiency optimization',
    ],
  },
];

export const allServices = [...constructionServices, ...engineeringServices];
